<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import {
  PERFIS_FANDATA,
  papelDoPerfil,
} from 'dashboard/helper/fandataPerfis';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  // T-263: o perfil FanData que está gravado hoje ('admin' quando é só administrador).
  fandataPerfil: {
    type: String,
    default: '',
  },
  // T-104: teto de atendimentos simultâneos e quanto o agente já tem na mão.
  limiteSimultaneos: {
    type: Number,
    default: null,
  },
  atendimentosAbertos: {
    type: Number,
    default: 0,
  },
  // O padrão da conta deixou de ser dez fixo em 13/10, a pedido do clube ("limitado a 5 por
  // vez"). Vem do servidor para o texto de ajuda não mentir o número.
  padraoSimultaneos: {
    type: Number,
    default: 10,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
// T-263: o seletor deixou de ser "papel do Chatwoot" e passou a ser "perfil do clube". São seis,
// e cada um carrega o papel nativo que precisa por baixo. Continuar com dois seletores (papel e
// perfil) obrigaria quem cadastra a saber qual combinação é válida, e combinação inválida aqui
// vira gente sem acesso ao que precisa ou com acesso ao que não devia.
const selectedRoleId = ref(props.fandataPerfil || props.type || 'agente');
const agentCredentials = ref({ email: props.email });
// Vazio = sem teto. O backend trata 0 e nulo do mesmo jeito, e é 0 que o formulário manda ao
// limpar o campo (um nulo seria descartado no caminho).
const limiteSimultaneos = ref(props.limiteSimultaneos || null);

// QUANDO O AGENTE JÁ PASSOU DO TETO, A TELA PRECISA DIZER O QUE ISSO SIGNIFICA.
//
// Relato do clube em 13/10: "por mais que eu coloque a quantidade de atendimento por agente,
// está entrando mais que o configurado". O print mostra esta tela com limite 3 e, logo abaixo,
// o nosso próprio texto dizendo "hoje ele tem 9 em aberto". Lado a lado, sem explicação
// nenhuma, isso se lê como "o limite não funciona".
//
// O limite funciona. Ele impede a distribuição automática de MANDAR mais; ele não desatribui o
// que já está na mão da pessoa. Com fila acumulada, um agente fica acima do teto por tempo
// indeterminado, e a tela não contava essa parte.
const tetoEfetivo = computed(
  () => Number(limiteSimultaneos.value) || props.padraoSimultaneos
);
const acimaDoTeto = computed(
  () => props.atendimentosAbertos > tetoEfetivo.value
);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const currentUser = useMapGetter('getCurrentUser');
const { papelNativo } = useFandataPerfil();

// Rebaixamento de si mesmo (02/10). O seletor grava perfil e papel nativo juntos, então escolher
// Agente ou Backoffice para a própria conta tira o acesso de administrador no mesmo clique, e com
// ele a tela que desfaz a escolha. O servidor recusa; aqui o aviso aparece antes, junto do
// seletor, porque quem faz isso quase sempre só quer VER a tela do outro perfil.
const editandoASiMesmo = computed(() => currentUser.value?.id === props.id);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const perfis = PERFIS_FANDATA.map(p => ({
    id: p.chave,
    name: p.chave,
    label: t(`AGENT_MGMT.PERFIS.${p.chave.toUpperCase()}`),
  }));

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...perfis, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const vaiSeRebaixar = computed(() => {
  if (!editandoASiMesmo.value) return false;
  if (papelNativo.value !== 'administrator') return false;

  const escolhido = selectedRole.value?.name || '';
  // Papel customizado é do enterprise, que não usamos, e não passa pelo mapa de perfis.
  if (!escolhido || escolhido.startsWith('custom_')) return false;

  return papelDoPerfil(escolhido) === 'agent';
});

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.PRE_PAUSA'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.REUNIAO'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (vaiSeRebaixar.value) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      fandata_limite_simultaneos: Number(limiteSimultaneos.value) || 0,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      // O perfil manda nos dois campos: o papel nativo é o que a API cobra, o perfil é o que a
      // tela mostra. 'admin' grava perfil vazio, porque ele é a ausência de refinamento sobre o
      // papel de administrador.
      payload.role = papelDoPerfil(selectedRole.value.name);
      payload.fandata_perfil =
        selectedRole.value.name === 'admin' ? '' : selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    // A chave da recusa do servidor é `error`, não `message`. Mostrar a dele importa aqui: a
    // única recusa própria desta tela (o auto-rebaixamento) explica o que fazer no lugar, e a
    // mensagem genérica jogaria isso fora.
    useAlert(
      error?.response?.data?.error || t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
        <div
          v-if="vaiSeRebaixar"
          class="p-2 mb-4 text-xs rounded-lg bg-n-amber-3 text-n-amber-12 border border-n-amber-5"
        >
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.AUTO_REBAIXAMENTO') }}
        </div>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.EDIT.FORM.LIMITE_SIMULTANEOS.LABEL') }}
          <input
            v-model="limiteSimultaneos"
            type="number"
            min="0"
            :placeholder="
              $t('AGENT_MGMT.EDIT.FORM.LIMITE_SIMULTANEOS.PLACEHOLDER', {
                padrao: padraoSimultaneos,
              })
            "
          />
          <span class="text-xs text-n-slate-11">
            {{
              $t('AGENT_MGMT.EDIT.FORM.LIMITE_SIMULTANEOS.HELP', {
                abertos: atendimentosAbertos,
                padrao: padraoSimultaneos,
              })
            }}
          </span>
          <span v-if="acimaDoTeto" class="text-xs text-n-amber-11">
            {{
              $t('AGENT_MGMT.EDIT.FORM.LIMITE_SIMULTANEOS.ACIMA_DO_TETO', {
                abertos: atendimentosAbertos,
                teto: tetoEfetivo,
              })
            }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
